/**
 * One Auth0 `/authorize` navigation per page visit (NCP-5357).
 *
 * Auth0 keeps only the MOST RECENTLY issued passwordless code alive for an
 * email + connection and silently invalidates every earlier one. So a second
 * trip to `/authorize` inside a single visit mints a second OTP and kills the
 * code already sitting in the customer's inbox — and because the two emails
 * are identical apart from the digits (and SES delivery order does not match
 * Auth0 issue order) the customer cannot tell which one is live. They enter
 * the dead code and Auth0 rejects it.
 *
 * `loginWithRedirect` is asynchronous (it builds the authorize URL before
 * calling `window.location.assign`), so a remount can call it again while the
 * first navigation is still in flight — both requests reach Auth0 even though
 * only the second document ever renders. A per-mount ref therefore cannot
 * close this; the flag has to outlive the component.
 *
 * WHY MODULE STATE rather than sessionStorage: "one page visit" is exactly the
 * lifetime of this module. A real navigation to Auth0 discards it, so the
 * return trip — and any later login in the same tab, e.g. after a logout —
 * starts from a clean slate. sessionStorage would persist past those and block
 * a legitimate second login.
 */
let redirectStarted = false;

export const hasLoginRedirectStarted = (): boolean => redirectStarted;

export const markLoginRedirectStarted = (): void => {
  redirectStarted = true;
};

/**
 * Release the guard. Called when a redirect attempt fails (nothing navigated,
 * so a retry must be allowed) and by tests between cases.
 */
export const resetLoginRedirectGuard = (): void => {
  redirectStarted = false;
};
